import { Skeleton } from 'antd';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/** Imita un MetricCard con icono a la derecha */
function SkeletonMetricCard() {
  return (
    <Card className="border-slate-200/60 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton.Input active size="small" style={{ width: '75%', height: 14 }} />
            <Skeleton.Input active size="small" style={{ width: '55%', height: 28 }} />
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-50 border border-slate-100 flex-shrink-0">
            <Skeleton.Avatar active size={20} shape="circle" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Imita el encabezado de sección (icono + título + subtítulo) */
function SkeletonSectionHeader({ titleWidth = 200, subtitleWidth = 340 }: { titleWidth?: number; subtitleWidth?: number }) {
  return (
    <CardTitle className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Skeleton.Avatar active size={24} shape="circle" />
      </div>
      <div className="space-y-1.5">
        <Skeleton.Input active size="small" style={{ width: titleWidth, height: 24 }} />
        {/* <Skeleton.Input active size="small" style={{ width: subtitleWidth, height: 14 }} /> */}
      </div>
    </CardTitle>
  );
}

export default function ReportsSkeleton() {
  return (
    <div className="space-y-6">

      {/* ═══ LEADS ═══ */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <SkeletonSectionHeader titleWidth={220} subtitleWidth={340} />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => <SkeletonMetricCard key={i} />)}
          </div>
        </CardContent>
      </Card>

      {/* ═══ PIPELINE ═══ */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <SkeletonSectionHeader titleWidth={100} subtitleWidth={320} />
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Gráficos: PipelineCoverageChart + StageConversionChart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* PipelineCoverageChart */}
              <Card className="border-slate-200/60 shadow-sm">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Skeleton.Input active size="small" style={{ width: 180, height: 22 }} />
                      <Skeleton.Input active size="small" style={{ width: 90, height: 22 }} />
                    </div>
                    {/* Gráfico de barras horizontales */}
                    <div className="h-[280px] w-full flex flex-col justify-center space-y-8 px-4">
                      <div className="space-y-2">
                        <Skeleton.Input active size="small" style={{ width: 120, height: 14 }} />
                        <div className="h-12 bg-slate-100 rounded-lg overflow-hidden">
                          <div className="h-full w-4/5 bg-slate-200 rounded-lg animate-pulse" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Skeleton.Input active size="small" style={{ width: 100, height: 14 }} />
                        <div className="h-12 bg-slate-100 rounded-lg overflow-hidden">
                          <div className="h-full w-3/5 bg-slate-200 rounded-lg animate-pulse" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="text-center space-y-1.5">
                          <Skeleton.Input active size="small" style={{ width: '100%', height: 13 }} />
                          <Skeleton.Input active size="small" style={{ width: '70%', height: 18 }} />
                        </div>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex justify-between">
                        <Skeleton.Input active size="small" style={{ width: 120, height: 14 }} />
                        <Skeleton.Input active size="small" style={{ width: 36, height: 14 }} />
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full w-3/5 bg-slate-200 rounded-full animate-pulse" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* StageConversionChart */}
              <Card className="border-slate-200/60 shadow-sm">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <Skeleton.Input active size="small" style={{ width: 160, height: 22 }} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                      <div className="flex items-center justify-center">
                        <Skeleton.Avatar active size={200} shape="circle" />
                      </div>
                      <div className="space-y-2.5">
                        <Skeleton.Input active size="small" style={{ width: 130, height: 14 }} />
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50/80 rounded-lg">
                            <div className="flex items-center gap-2.5">
                              <div className="w-2.5 h-2.5 rounded-full bg-slate-200 animate-pulse" />
                              <Skeleton.Input active size="small" style={{ width: 80, height: 14 }} />
                            </div>
                            <Skeleton.Input active size="small" style={{ width: 36, height: 18 }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* MetricCards: 4 normales + 1 stage breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {[1, 2, 3, 4].map((i) => <SkeletonMetricCard key={i} />)}

              {/* Stage breakdown card */}
              <Card className="border-slate-200/60 shadow-sm">
                <CardContent className="p-4">
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0">
                        <Skeleton.Avatar active size={14} shape="circle" />
                      </div>
                      <Skeleton.Input active size="small" style={{ width: 130, height: 14 }} />
                    </div>
                    <div className="space-y-1">
                      {[1, 2, 3].map((j) => (
                        <div key={j}>
                          <div className="flex justify-between items-center py-1.5">
                            <Skeleton.Input active size="small" style={{ width: 70, height: 14 }} />
                            <Skeleton.Input active size="small" style={{ width: 36, height: 18 }} />
                          </div>
                          {j < 3 && <div className="h-px bg-slate-100" />}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ═══ CICLO DE VENTA (TIME) ═══ */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <SkeletonSectionHeader titleWidth={160} subtitleWidth={340} />
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tiempo promedio por etapa */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Skeleton.Avatar active size={20} shape="circle" />
                    <Skeleton.Input active size="small" style={{ width: 220, height: 22 }} />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <div className="flex-1 space-y-1">
                            <Skeleton.Input active size="small" style={{ width: 130, height: 18 }} />
                            <Skeleton.Input active size="small" style={{ width: 90, height: 14 }} />
                          </div>
                          <Skeleton.Input active size="small" style={{ width: 48, height: 26 }} />
                        </div>
                        <div className="relative h-8 bg-gradient-to-r from-slate-100 to-slate-50 rounded-lg overflow-hidden border border-slate-200">
                          <div
                            className="absolute inset-y-0 left-0 bg-slate-200 rounded-lg animate-pulse"
                            style={{ width: `${55 + i * 10}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Columna derecha: 3 MetricCards + warning card */}
              <div className="space-y-4">
                {[1, 2, 3].map((i) => <SkeletonMetricCard key={i} />)}
                <Card className="border-orange-200 bg-orange-50/50">
                  <CardContent className="pt-6 pb-4">
                    <div className="flex items-center gap-2">
                      <Skeleton.Avatar active size={16} shape="circle" />
                      <Skeleton.Input active size="small" style={{ width: 220, height: 18 }} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Tabla oportunidades estancadas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Skeleton.Avatar active size={20} shape="circle" />
                  <Skeleton.Input active size="small" style={{ width: 310, height: 22 }} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex justify-between items-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex-1 space-y-2">
                        <Skeleton.Input active size="small" style={{ width: 200, height: 18 }} />
                        <div className="flex gap-4">
                          <Skeleton.Input active size="small" style={{ width: 80, height: 14 }} />
                          <Skeleton.Input active size="small" style={{ width: 130, height: 14 }} />
                        </div>
                      </div>
                      <Skeleton.Input active size="small" style={{ width: 80, height: 26 }} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* ═══ REVENUE / INGRESOS ═══ */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <SkeletonSectionHeader titleWidth={110} subtitleWidth={320} />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <SkeletonMetricCard key={i} />)}
            </div>
            {/* Goal progress card */}
            <Card className="border-slate-200/60">
              <CardContent className="p-4">
                <div className="space-y-3">
                  <Skeleton.Input active size="small" style={{ width: 200, height: 16 }} />
                  <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full w-2/3 bg-slate-200 rounded-full animate-pulse" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="text-center space-y-1.5">
                        <Skeleton.Input active size="small" style={{ width: '70%', height: 12 }} />
                        <Skeleton.Input active size="small" style={{ width: '55%', height: 18 }} />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* ═══ ACTIVIDAD ═══ */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <SkeletonSectionHeader titleWidth={110} subtitleWidth={260} />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((i) => <SkeletonMetricCard key={i} />)}
          </div>
        </CardContent>
      </Card>

      {/* ═══ CLIENTES ═══ */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <SkeletonSectionHeader titleWidth={110} subtitleWidth={310} />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => <SkeletonMetricCard key={i} />)}
            </div>
            {/* Retention details + CLV details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ['Detalles de Retención', ['Total Clientes', 'Clientes Recurrentes', 'Clientes Nuevos']],
                ['Detalles CLV', ['Ticket Promedio', 'Frecuencia de Compra', 'Meses de Retención']],
              ].map(([_title, rows], cardIdx) => (
                <Card key={cardIdx} className="border-slate-200/60">
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <Skeleton.Input active size="small" style={{ width: 160, height: 16 }} />
                      {(rows as string[]).map((_, rowIdx) => (
                        <div key={rowIdx}>
                          <div className="flex justify-between items-center py-1.5">
                            <Skeleton.Input active size="small" style={{ width: 110, height: 13 }} />
                            <Skeleton.Input active size="small" style={{ width: 50, height: 18 }} />
                          </div>
                          {rowIdx < (rows as string[]).length - 1 && <div className="h-px bg-slate-100" />}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ═══ CHARTS / GRÁFICOS ═══ */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <SkeletonSectionHeader titleWidth={100} subtitleWidth={360} />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pipeline por Etapa */}
            <Card className="border-slate-200/60">
              <CardHeader className="pb-3">
                <Skeleton.Input active size="small" style={{ width: 180, height: 16 }} />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex justify-between">
                        <Skeleton.Input active size="small" style={{ width: 100, height: 13 }} />
                        <Skeleton.Input active size="small" style={{ width: 100, height: 13 }} />
                      </div>
                      <div className="h-6 bg-slate-100 rounded overflow-hidden">
                        <div
                          className="h-full bg-slate-200 rounded animate-pulse"
                          style={{ width: `${90 - i * 15}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-slate-100">
                    <Skeleton.Input active size="small" style={{ width: 140, height: 13 }} />
                    <Skeleton.Input active size="small" style={{ width: 80, height: 13 }} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tendencia de Ventas */}
            <Card className="border-slate-200/60">
              <CardHeader className="pb-3">
                <Skeleton.Input active size="small" style={{ width: 160, height: 16 }} />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <Skeleton.Input active size="small" style={{ width: 80, height: 16 }} />
                      <div className="text-right space-y-1">
                        <Skeleton.Input active size="small" style={{ width: 90, height: 18 }} />
                        <Skeleton.Input active size="small" style={{ width: 60, height: 13 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Motivos de Pérdida */}
            <Card className="border-slate-200/60 lg:col-span-2">
              <CardHeader className="pb-3">
                <Skeleton.Input active size="small" style={{ width: 180, height: 16 }} />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-100">
                      <div className="space-y-1">
                        <Skeleton.Input active size="small" style={{ width: 180, height: 16 }} />
                        <Skeleton.Input active size="small" style={{ width: 130, height: 13 }} />
                      </div>
                      <Skeleton.Input active size="small" style={{ width: 80, height: 18 }} />
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-slate-100">
                    <Skeleton.Input active size="small" style={{ width: 120, height: 13 }} />
                    <Skeleton.Input active size="small" style={{ width: 80, height: 13 }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
